
import { AlertCircle, Download, FileUp } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ImportDialogContentProps {
  file: File | null;
  preview: any[];
  hasError: boolean;
  errorMessage: string;
  isLoading: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImport: () => void;
  onClose: () => void;
}

export const ImportDialogContent = ({
  file,
  preview,
  hasError,
  errorMessage,
  isLoading,
  onFileChange,
  onImport,
  onClose,
}: ImportDialogContentProps) => {
  const downloadSampleFile = () => {
    window.open('/sample_users_import.csv', '_blank');
  };

  return (
    <DialogContent className="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>Import Users from File</DialogTitle>
        <DialogDescription>
          Upload a CSV or Excel file with user data. The file must include the following columns: 
          email, mobile_number, school_id, role.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-4">
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadSampleFile}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Download Sample Template
          </Button>
        </div>
        
        <div className="flex items-center justify-center border-2 border-dashed rounded-lg p-8 border-gray-300">
          <label className="flex flex-col items-center justify-center cursor-pointer">
            <FileUp className="h-10 w-10 text-gray-400 mb-2" />
            <span className="text-sm text-gray-500 mb-2">
              {file ? file.name : "Click to upload or drag and drop"}
            </span>
            <span className="text-xs text-gray-400">
              CSV, XLS, XLSX files supported
            </span>
            <input
              type="file"
              className="hidden"
              accept=".csv,.xls,.xlsx"
              onChange={onFileChange}
            />
          </label>
        </div>

        {hasError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        {preview.length > 0 && (
          <div className="overflow-x-auto">
            <h3 className="text-sm font-medium mb-2">Data Preview (First 5 rows):</h3>
            <table className="min-w-full border">
              <thead className="bg-gray-50">
                <tr>
                  {Object.keys(preview[0]).slice(0, 5).map((header, idx) => (
                    <th key={idx} className="px-3 py-2 text-xs text-gray-500 border">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {Object.keys(row).slice(0, 5).map((key, cellIdx) => (
                      <td key={cellIdx} className="px-3 py-2 text-xs border">
                        {String(row[key] || '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button 
          variant="outline" 
          onClick={onClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          onClick={onImport}
          disabled={!file || hasError || isLoading}
          className="gap-2"
        >
          {isLoading ? (
            <>
              <span className="animate-spin">↻</span>
              Importing...
            </>
          ) : (
            <>
              <FileUp className="h-4 w-4" />
              Import Data
            </>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
